import type { AssessArea, GradeBand, OrgUseCase } from 'modules/auth/onboarding';

export type TemplateLanguage = 'uz' | 'ru' | 'en';
export type TemplateLevel = 'beginner' | 'intermediate' | 'advanced';

export const TEMPLATE_LANGUAGES: readonly TemplateLanguage[] = ['uz', 'ru', 'en'];

export interface TemplateSummary {
  id: string;
  key: string;
  language: TemplateLanguage;
  title: string;
  description: string;
  useCases: OrgUseCase[];
  assessAreas: AssessArea[];
  gradeBands: GradeBand[];
  level: TemplateLevel;
  timeLimitMinutes: number;
  passingScore: number;
  questionCount: number;
  questionTypes: string[];
  /** FEATURES keys the plan needs for "Use" (e.g. advanced question types). */
  requiredFeatures: string[];
  featured: boolean;
  usageCount: number;
  /** PLATFORM = Examing-curated; ORG = saved by this organization. */
  scope: 'PLATFORM' | 'ORG';
  /** Set on ORG templates only. */
  createdBy?: string;
  /** Set on exam templates (they create an IELTS test instead of a subject-builder test). */
  examStandard?: 'IELTS';
}

export type TemplateSource = 'platform' | 'org';

export interface TemplateQuestion {
  type: string;
  prompt: string;
  options?: Array<{ id: string; value: string }>;
  matchPairs?: Array<{ key: string; value: string }>;
  passageTitle?: string;
  subQuestions?: Array<{ id: string; questionText?: string }>;
}

export interface TemplateExamSection {
  kind: string;
  questions: TemplateQuestion[];
}

export interface TemplateSubject {
  name: string;
  questionCount: number;
  pointsPerQuestion: number;
}

export interface TemplateDetail extends TemplateSummary {
  subjectName: string;
  pointsPerQuestion: number;
  questions: TemplateQuestion[];
  /** Multi-subject templates: each becomes its own subject in the copied test. */
  subjects?: TemplateSubject[];
  examSections?: TemplateExamSection[];
}

export interface TemplateListParams {
  useCase?: OrgUseCase;
  area?: AssessArea;
  language?: TemplateLanguage;
  search?: string;
  source?: TemplateSource;
  page?: number;
  limit?: number;
}

export interface TemplateListResponse {
  data: TemplateSummary[];
  total: number;
  page: number;
  limit: number;
}

export interface UseTemplateResult {
  testId: string;
  /** Absent for exam templates. */
  subjectId?: string;
}

/** Why a template was recommended (mirrors the API's RecommendationReason). */
export type RecommendationReason =
  | { kind: 'use_case'; key: OrgUseCase }
  | { kind: 'area'; key: AssessArea }
  | { kind: 'grade_band'; key: GradeBand }
  | { kind: 'role'; key: string };

export interface RecommendedTemplate extends TemplateSummary {
  reasons: RecommendationReason[];
}

export interface RecommendedTemplatesResponse {
  data: RecommendedTemplate[];
  /** False when the org gave no usable onboarding answers (list = featured/popular). */
  basedOnProfile: boolean;
}
